import { useMutation, useQuery } from '@zg-rentals/react-query5';
import type { AutocompleteResult } from '@/src/lib/api/endpoints/area';
import { type RecentSearchUpdateBody, user } from '@/src/lib/api/endpoints/user';
import type { FilterState } from '@/src/lib/filters/filterTypes';
import { recentSearchToArea, recentSearchToFilterState } from '@/src/lib/recentSearch';
import { RECENT_SEARCH_TAG } from './queryTags';

import { recentSearchKeys } from './queryKeys';

// Null fields mean the user has no usable recent search for that part.
export type RecentSearchSeed = {
  filters: FilterState | null;
  area: AutocompleteResult | null;
};

export const useRecentSearch = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: recentSearchKeys.search(),
    queryFn: async (): Promise<RecentSearchSeed> => {
      const response = await user.getRecentSearch();
      // v2 envelopes report failure as HTTP 200 + success:false; don't cache that as an empty seed.
      if (!response.success || !response.loggedIn) {
        throw new Error('recentSearch returned an unsuccessful envelope');
      }
      return {
        filters: recentSearchToFilterState(response.data),
        area: recentSearchToArea(response.data),
      };
    },
    enabled: options?.enabled ?? false,
    staleTime: 30_000,
    meta: {
      tags: [RECENT_SEARCH_TAG],
      context: { api: 'hotpads-api', details: 'fetch recent search' },
    },
  });
};

export const useUpdateRecentSearch = () => {
  return useMutation({
    mutationFn: (body: RecentSearchUpdateBody) => user.updateRecentSearch(body),
    meta: {
      // Refresh the homepage seed (HP-8425) after each persisted search.
      invalidates: [RECENT_SEARCH_TAG],
      context: {
        api: 'hotpads-api',
        details: 'update recent search',
      },
    },
  });
};
